<!-- eslint-disable vue/no-v-html -->
<!-- 
  The html content is sanitized using DOMPurify before rendering it in the component.
  so that we can safely render the markdown content without worrying about XSS attacks.
  Disable the eslint rule vue/no-v-html for this file to avoid the warning.
 -->
<template>
  <div @click="handleLinkClick" v-html="sanitizedHTML"></div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import markdownIt from 'markdown-it';
import markdownItLinkAttributes from 'markdown-it-link-attributes';
import DOMPurify from 'dompurify';

const props = defineProps<{
  markdown: string;
}>();

const router = useRouter();

const isExternalLink = (href: string): boolean => {
  if (href.startsWith('/')) return false;
  try {
    return new URL(href, window.location.origin).origin !== window.location.origin;
  } catch {
    return true;
  }
};

const md = new markdownIt();
md.use(markdownItLinkAttributes, [
  {
    matcher(href: string): boolean {
      return isExternalLink(href);
    },
    attrs: {
      target: '_blank',
      rel: 'noopener noreferrer',
    },
  },
]);

const sanitizedHTML = computed(() => {
  const renderedMarkdown: string = md.render(props.markdown);
  return DOMPurify.sanitize(renderedMarkdown, {
    ADD_ATTR: ['target', 'rel'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  });
});

const handleLinkClick = (event: MouseEvent): void => {
  const anchor = (event.target as HTMLElement).closest('a');
  if (!anchor) return;

  if (!anchor.hasAttribute('target')) {
    event.preventDefault();
    const url = new URL(anchor.href);
    router.push(url.pathname + url.search + url.hash);
  }
};
</script>
